import * as Yup from "yup";

import { Modal, OfficeButton } from "components";
import UpdateTargetRateForm, {
  UpdateTargetRateFormValues,
} from "components/forms/update-target-rate";
import { useEffect, useRef } from "react";

import isEqual from "lodash/isEqual";
import toast from "react-hot-toast";
import { updateTargetRate } from "../../_graphql/visal/mutations/target-rate";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useTargetRate } from "../../hooks/data/use-target-rates";
import { wrapClick } from "utils";

const validationSchema = Yup.object().shape({
  rate_value: Yup.number()
    .required("Rate value is required")
    .min(0, "Must be at least 0"),
  currency_code: Yup.string().required("Currency code is required"),
  currency: Yup.string().required("Currency name is required"),
  rateId: Yup.string().required(),
});

export default function UpdateTargetRateContainer({
  open,
  setOpen,
  rateId,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  rateId: string;
}) {
  const { loading, targetRate, refetch } = useTargetRate(rateId);
  const previousValues = useRef<UpdateTargetRateFormValues | null>(null);

  const [execute, { loading: updating }] = useMutation(updateTargetRate, {
    refetchQueries: ["fetchTargetRate", "fetchTargetRates"],
  });

  const form = useFormik<UpdateTargetRateFormValues>({
    initialValues: {
      rate_value: 0,
      currency_code: "",
      currency: "",
      rateId: rateId || "",
    },
    validationSchema,
    onSubmit: async (variables) => {
      try {
        const { data } = await execute({
          variables: {
            targetRateId: variables.rateId,
            rate: {
              rate_value: variables.rate_value,
              currency_code: variables.currency_code,
              currency: variables.currency,
            },
          },
        });

        if (data?.updateTargetRate) {
          toast.success("Rate updated successfully");
          setOpen(false);
          form.resetForm();
          refetch();
        } else {
          toast.error("Failed to update Rate");
        }
      } catch (err: unknown) {
        toast.error(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
      }
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (targetRate) {
      const newValues = {
        rate_value: targetRate.rate_value || 0,
        currency_code: targetRate.currency_code || "",
        currency: targetRate.currency || "",
        rateId: rateId || "",
      };

      // Only update if values are different from current values
      if (
        !isEqual(newValues, form.values) &&
        !isEqual(newValues, previousValues.current)
      ) {
        previousValues.current = newValues;
        form.setValues(newValues, false); 
      }
    }
  }, [targetRate, rateId]); 

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      title="Update Rate"
      description="Update rate value and currency information"
      renderActions={() => (
        <OfficeButton
          type="submit"
          size="sm"
          loading={updating}
          disabled={updating}
          onClick={wrapClick(form.handleSubmit)}
        >
          {updating ? "Please wait..." : "Update Rate"}
        </OfficeButton>
      )}
    >
      <UpdateTargetRateForm form={form} />
    </Modal>
  );
}
